import { useCallback, useContext, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { RootState, useAppDispatch, useAppSelector } from '../store';
import { UserSettings } from '../Types';
import { SocketContext } from '../SocketContext';
import { ChatList } from '../Types';
import { setChatLoaded, setIsNewChat, setIsSending } from '../Store/features/chat/chatSlice';
import { setChatlist } from '../Store/features/chatlist/chatlistSlice';
import { chatService } from '../Services/chatService';
import { store } from '../store';
import { setNavigationSubTitle, setNavigationTitle } from '../Store/features/navigationHeader/navigationHeaderSlice';
import { StoreChat } from '../Types/StoreTypes';
import toast from 'react-hot-toast';
import { EMPTY_CHAT_MESSAGES } from '../Store/selectors/chatSelectors';

const MAX_STREAMING_PER_USER = 3;

const useSocketMessageHandler = () => {
    const navigate = useNavigate();
    const dispatch = useAppDispatch();
    const userSettings = useAppSelector((state: RootState) => state.userSettings as UserSettings);
    const i18n = useAppSelector((state: RootState) => state.i18n.i18n);
    const { socket } = useContext(SocketContext);

    const selectedLanguage = useAppSelector((state: RootState) => state.i18n.selectedLanguage);

    const sendSocketMessage = useCallback(
        async (
            chatIdentifier: string = '',
            message: string = '',
            dlpActive: boolean = true,
            images: any[] = [{}],
            files: any[] = [{}],
            callbacks: Array<() => void> = [],
        ) => {
            const filesWithoutUrl = files.map((file: any) => {
                if (file && file.sha1) {
                    return {
                        sha1: file.sha1,
                        name: file.name,
                    };
                }
                return file;
            });

            const params = {
                userContent: message,
                type: 'sendMessage',
                chatIdentifier,
                dlpActive,
                images,
                files: filesWithoutUrl,
                language: selectedLanguage,
            };

            socket?.send(JSON.stringify(params));
            callbacks.forEach((callback) => callback());
        },
        [socket, selectedLanguage],
    );

    const sendMessage = useCallback(
        async (
            chatId: string = '',
            message: string = '',
            promptIdentifier: string = '',
            chats: ChatList[],
            dlpActive: boolean = true,
            images: any[] = [{}],
            files: any[] = [{}],
            callbacks: Array<() => void> = [],
        ) => {
            const chatMessages = (store.getState().chats as StoreChat).chatMessages;
            const isChatSending = !!(Object.values(chatMessages[chatId] ?? EMPTY_CHAT_MESSAGES)[Object.values(chatMessages[chatId] ?? EMPTY_CHAT_MESSAGES).length - 1] as any)?.isSending;
            const isMaxStreamingReached =  Object.values(chatMessages).filter((messages:any) => {
                messages = Object.values(messages);
                return !!messages[messages.length - 1]?.isSending;
            }).length >= MAX_STREAMING_PER_USER;
            if (isChatSending || isMaxStreamingReached || !message) {
                if (isMaxStreamingReached) {
                    toast.error(i18n.translate({ id: 'toast.maxStreamingReached', sourceName: 'Chat', params: {count: MAX_STREAMING_PER_USER} }));
                }
                return;
            }

            dispatch(setIsSending(true));

            if (!chatId) {
                try {
                    const response = await chatService.createChat(promptIdentifier, dlpActive);

                    const chatData = {
                        assistantTitle: response.data.prompt_name,
                        assistantImage: response.data.prompt_image,
                        ...response.data,
                    };

                    dispatch(setNavigationSubTitle(''));
                    dispatch(setNavigationTitle(''));
                    dispatch(setIsNewChat(true));
                    dispatch(setChatLoaded(false));
                    dispatch(setChatlist([chatData, ...chats]));
                    navigate(`/chats/${response.data.identifier}`);
                    while (!(store.getState().chats as StoreChat).chatLoaded) {
                        await new Promise((resolve) => setTimeout(resolve, 5));
                    }
                    await sendSocketMessage(response.data.identifier, message, dlpActive, images, files, callbacks);
                } catch (error) {
                    console.error('Error creating chat:', error);
                }
            } else {
                while (!(store.getState().chats as StoreChat).chatLoaded) {
                    await new Promise((resolve) => setTimeout(resolve, 5));
                }
                await sendSocketMessage(chatId, message, dlpActive, images, files, callbacks);
            }
        },
        [dispatch, navigate, sendSocketMessage, i18n],
    );

    const mayEditMessage = useCallback(
        async (
            chatIdentifier: string,
            callback: () => void
        ) => {
            const chatMessages = (store.getState().chats as StoreChat).chatMessages;
            const isChatSending = !!(Object.values(chatMessages[chatIdentifier] ?? EMPTY_CHAT_MESSAGES)[Object.values(chatMessages[chatIdentifier] ?? EMPTY_CHAT_MESSAGES).length - 1] as any)?.isSending;
            const isMaxStreamingReached =  Object.values(chatMessages).filter((messages:any) => {
                messages = Object.values(messages);
                return !!messages[messages.length - 1]?.isSending;
            }).length >= MAX_STREAMING_PER_USER;
            if (isChatSending || isMaxStreamingReached) {
                if (isMaxStreamingReached) {
                    toast.error(i18n.translate({ id: 'toast.maxStreamingReached', sourceName: 'Chat', params: {count: MAX_STREAMING_PER_USER} }));
                }
                return;
            }

            callback();
        },
        [i18n],
    );

    const stopStream = useCallback(async (chatIdentifier: string) => {
        const chatMessages = (store.getState().chats as StoreChat).chatMessages;
        const isSending = !!(Object.values(chatMessages[chatIdentifier] ?? EMPTY_CHAT_MESSAGES)[Object.values(chatMessages[chatIdentifier] ?? EMPTY_CHAT_MESSAGES).length - 1] as any)?.isSending;
        const isStopable = !!(Object.values(chatMessages[chatIdentifier] ?? EMPTY_CHAT_MESSAGES)[Object.values(chatMessages[chatIdentifier] ?? EMPTY_CHAT_MESSAGES).length - 1] as any)?.isStopable;
        if (!isSending && isStopable) {
            return;
        }

        let params = {
            email: userSettings.email ?? '',
            type: 'stopStream',
            language: selectedLanguage,
            chatIdentifier,
        };

        dispatch(setIsSending(false));

        socket?.send(JSON.stringify(params));
    }, [userSettings.email, selectedLanguage, socket, dispatch]);

    const checkFiles = useCallback(
        async (sha1Files: string[]) => {
            let params = {
                email: userSettings.email ?? '',
                sha1Files: sha1Files,
                type: 'checkFiles',
                language: selectedLanguage,
            };

            dispatch(setIsSending(false));

            socket?.send(JSON.stringify(params));
        },
        [socket, dispatch, selectedLanguage, userSettings.email],
    );

    const SocketMessageHandlerActions = useMemo(
        () => ({
            sendSocketMessage,
            sendMessage,
            mayEditMessage,
            stopStream,
            checkFiles,
        }),
        [sendSocketMessage, sendMessage, mayEditMessage, stopStream, checkFiles],
    );

    return { SocketMessageHandlerActions };
};

export default useSocketMessageHandler;
